/**
 * @file JS - Class - SingleMapPage.js
 * @author Adam Lemire
 * @since 2019/06/13
 * @version 1.0
 * @summary Dernières modifications : 2021/06/15
 */

import Page, { PageType } from './Page.js';
import RasterLayer from './../Layer/RasterLayer.js';
import VectorLayer from '../Layer/VectorLayer.js';
import MapOL from 'ol/Map.js';
import View from 'ol/View.js';
import { EPSG, StyleTypes } from './../../helpers/Constants.js';
import {
  defaults as defaultControls,
  ScaleLine
} from 'ol/control.js';
// import { jsPDF as JsPDF } from 'jspdf';
import LayoutPage from './LayoutPage/LayoutPage'; // eslint-disable-line no-unused-vars
import { gisSettings } from '../../gisSettings.js';
import { Evenements } from '../../events/Evenements.js';

const COORDONNEES_CENTRE = [-8000000, 6000000];
const OPTION_LIBELLÉ = gisSettings.options.layer.coucheLabel.active;

/**
 * @typedef SingleMapPage
 * @classdesc Classe représentant le contenu de la section d'une carte simple
 * @augments Page
 * */
export default class SingleMapPage extends Page {
  /**
   * @example new SingleMapPage( { id: pageId, title: pageName, targetMap: mapSectionId } )
   * @param { object } options - Options du constructeur
   * @param { number } options.id - Id de la carte
   * @param { string } options.title - Titre de la carte
   * @param { string } options.targetMap - Conteneur html de la map
   * @param { object } options.jsonSingleMapPage - Seulement lorsque créé par l'ouverture d'un projet existant
   */
  constructor ({ id, title, targetMap, jsonSingleMapPage = false }) {
    if (jsonSingleMapPage) {
      super(jsonSingleMapPage.id, jsonSingleMapPage.title, PageType.SINGLEMAPPAGE);
      this._créerDepuisJSON(jsonSingleMapPage);
    } else {
      super(id, title, PageType.SINGLEMAPPAGE);
      /** @type { Map < number, RasterLayer | VectorLayer > } */
      this._layers = new Map();
      /** @type { Map < number, number > } - zIndex, layerId */
      this._layersZIndex = new Map();
      this._selectedLayer = null;
      /** @type { number } */
      this._layerCount = 0;
      /** @type { string } */
      this._targetMap = targetMap;
      /**
       * @type { View }
       * @description constrainResolution: true fait en sorte que zoom toujours entier, donc pas de probleme de bordure de tuiles
       * */
      this._view = new View({
        projection: EPSG.default,
        center: COORDONNEES_CENTRE,
        zoom: 6,
        constrainResolution: true
      });
      /** @type { MapOL } */
      this._map = new MapOL({
        controls: defaultControls({
          attribution: false,
          rotate: false,
          zoom: false
        }).extend([
          new ScaleLine({
            units: 'metric'
          })
        ]),
        target: targetMap,
        view: this._view
      });
      /** @type { LayoutPage } */
      this._layoutPage = new LayoutPage({ id: id, nomFichier: title });
    }
    /** @type { RasterLayer | VectorLayer } */
    this._selectedLayer = null;
    /** @type { Map < number, DataPage > } */
    this._attributeTables = new Map();
    /** @type { Map < number, number > } - zIndex, layerId */
    this._attributeTablesZIndex = new Map(); // ! ne sert pas pour le moment, et n'est pas mis a jour lorsque tables enlevées
  }

  get title () { return this._title; }

  set title (title) {
    this._title = title;
    this._notifyTitleChanged(title);
    if (this._layoutPage) {
      this._layoutPage.nomFichier = title;
    }
  }

  get olMap () { return this._map; }

  get type () { return this._type; }

  get layers () { return this._layers; }

  get layersZIndex () { return this._layersZIndex; }

  get attributeTables () { return this._attributeTables; }

  get target () { return this._map.getTarget(); }

  get selectedLayer () { return this._selectedLayer; }

  get idSelectedFeatures () { return this._selectedLayer.idSelectedFeatures; }

  get layoutPage () { return this._layoutPage; }

  set layoutPage (layoutPage) { this._layoutPage = layoutPage; }

  /*
   * PUBLIC METHODS
   */

  activer () {
    this._layoutPage.creerConteneurInterne();
    this._layoutPage.olMap = this._map;
  }

  supprimer () {
    this._attributeTables.forEach(function (dataPage) {
      Evenements.dataPageSupprimee.notify(dataPage);
    });
  }

  /**
   * @param { number } layerId
   * @returns { RasterLayer | VectorLayer }
   */
  async getLayer (layerId) {
    const layer = await this._layers.get(layerId);
    return layer;
  }

  zoomIn () {
    const view = this._map.getView();
    view.animate({
      zoom: view.getZoom() + 1,
      duration: 200
    });
  }

  zoomOut () {
    const view = this._map.getView();
    view.animate({
      zoom: view.getZoom() - 1,
      duration: 200
    });
  }

  /**
   * @param { object } webRasterLayer
   */
  async addRasterLayer (webRasterLayer) {
    const _this = this;
    const rasterLayer = await new RasterLayer({
      id: _this._layerCount + 1,
      name: webRasterLayer.name,
      sourceUrl: webRasterLayer.url,
      sourceName: webRasterLayer.name,
      sourceType: webRasterLayer.sourceType
    });
    const olLayer = await rasterLayer.olLayer;

    if (olLayer) {
      const layer = await this._ajouterLayerÀListe(rasterLayer);
      this._map.addLayer(layer);
      this._layerCount++;
    } else {
      console.log('olLayer n existe pas');
    }
  }

  /**
   * @param { string } name - name to display
   * @param { string } sourceWorkspace
   * @param { string } sourceName
   */
  async addVectorLayer (name, sourceWorkspace, sourceName) {
    try {
      const vectorLayer = await this._createVectorLayer(name, sourceWorkspace, sourceName);

      this._ajouterNouveauVectorLayer(vectorLayer);
    } catch (err) {
      console.log(err);
    }
  }

  async _ajouterCoucheLabel (vectorLayer, olLayerZIndex) {
    const avecLibellés = await vectorLayer.getLibellésExistent();
    if (avecLibellés) {
      const labelLayer = await vectorLayer.olLayerLibellés;
      this._map.addLayer(await labelLayer);
    }
  }

  /**
   * @param { string } data
   */
  async addVectorLayerFromData (data) {
    try {
      const vectorLayer = await this._créerVectorLayerDepuisDonnéesImportées(data);

      this._ajouterNouveauVectorLayer(vectorLayer);
    } catch (err) {
      console.log(err);
    }
  }

  /**
   * @param { number } layerId
   */
  async removeLayer (layerId) {
    const layer = await this._layers.get(layerId);
    const olLayer = await layer.olLayer;
    const zIndexToRemove = layer.zIndex;
    this._map.removeLayer(olLayer);
    if (OPTION_LIBELLÉ) {
      const olLayerLabel = await layer.olLayerLibellés;
      if (olLayerLabel) {
        this._map.removeLayer(olLayerLabel);
      }
    }
    this._layers.delete(layerId);

    let nextLayerId = this._layersZIndex.get(zIndexToRemove + 1);
    let nextLayerNewZIndex = zIndexToRemove;

    while (nextLayerId) {
      this.setLayerZIndex(nextLayerId, nextLayerNewZIndex);
      nextLayerNewZIndex++;
      nextLayerId = this._layersZIndex.get(nextLayerNewZIndex + 1);
    }
    this._layersZIndex.delete(nextLayerNewZIndex);
  }

  /** @param { DataPage } dataPage */
  addDataPageToAttributeTables (dataPage) {
    this._attributeTables.set(dataPage.id, dataPage);
    this._attributeTablesZIndex.set(this._attributeTables.size + 1, dataPage.id);
  }

  /** @param { number } layerId */
  selectLayerFromId (layerId) {
    this._selectedLayer = this._layers.get(layerId);
  }

  /** @param { FeatureLike [] } features */
  setIdSelectedFeatures (features) {
    const _this = this;

    if (this._selectedLayer.joinField) {
      const mIdSelectedFeatures = [];

      features.forEach(function (feature) {
        if (feature) mIdSelectedFeatures.push(feature.get(_this.selectedLayer.joinField));
      });
      this._selectedLayer.idSelectedFeatures = mIdSelectedFeatures;
    } else {
      this._selectedLayer.idSelectedFeatures = !features[0] || this._selectedLayer.idSelectedFeatures[0] === features[0].getId() ? [] : [features[0].getId()];
    }
  }

  /**
   * @param { FeatureLike } feature
   * @returns { string | number }
   */
  getIdFromFeature (feature) {
    if (!feature) return;

    const _this = this;

    if (this._selectedLayer.joinField) {
      return feature.get(_this.selectedLayer.joinField);
    } else {
      return feature.getId();
    }
  }

  /**
   * @param { number } layerId
   * @param { number } zIndex
   */
  async setLayerZIndex (layerId, zIndex) {
    const layer = await this._layers.get(layerId);
    const olLayer = await layer.olLayer;

    olLayer.setZIndex(zIndex);
    layer.zIndex = zIndex;
    this._layersZIndex.set(zIndex, layerId);
  }

  /**
   * @param { number[] } coordonnées
   */
  centerMap (coordonnées = COORDONNEES_CENTRE) {
    this._map.getView().setCenter(coordonnées);
  }

  exportToPDF () {
    this.exporterCarteUniqueVersPDF();
  }

  /** @param { string } target */
  changeTarget (target) {
    this.olMap.setTarget(target);
    this.refreshMap();
  }

  resetTarget () {
    this.olMap.setTarget(this._targetMap);
    this.olMap.setView(this._view);
  }

  /** @returns { View } View */
  get view () { return this._view; }

  /** @param { View } view */
  setTemporaryView (view) { this.olMap.setView(view); }

  /** @override */
  refreshView () {
    this.olMap.updateSize();
  }

  refreshMap () {
    this.olMap.updateSize();
    this.olMap.render();
  }

  updateSelectedLayerStyleFromSelectionChange (featureSelectionnee) {
    this.setIdSelectedFeatures([featureSelectionnee]);
    console.log(this._selectedLayer);
    this._selectedLayer.updateStyleFromSelectionChanged(featureSelectionnee);
  }

  modifierVectorLayer (objet) {
    const vectorLayer = objet.vectorLayer;
    const nouveauOlVectorLayer = objet.nouveauOlVectorLayer;

    this._map.removeLayer(vectorLayer.olLayer);
    vectorLayer.olLayer = nouveauOlVectorLayer;
    this._map.addLayer(nouveauOlVectorLayer);
    nouveauOlVectorLayer.setZIndex(vectorLayer.zIndex);
    this.refreshMap();
  }

  /*
   * PRIVATE METHODS
   */

  async _ajouterNouveauVectorLayer (vectorLayer) {
    const olLayer = await vectorLayer.olLayer;

    if (olLayer) {
      const layer = await this._ajouterLayerÀListe(vectorLayer);
      this._map.addLayer(await layer);
      this._layerCount++;

      if (OPTION_LIBELLÉ) {
        this._ajouterCoucheLabel(vectorLayer, layer.zIndex);
      }
    } else {
      console.log('olLayer n existe pas');
    }
  }

  /**
   * @param { string } name - name to display
   * @param { string } sourceWorkspace
   * @param { string } sourceName
   * @returns { VectorLayer }
   */
  _createVectorLayer (name, sourceWorkspace, sourceName) {
    const compteur = this._layerCount + 1;
    return new VectorLayer({
      id: compteur,
      name: name,
      sourceWorkspace: sourceWorkspace,
      sourceName: sourceName
    });
  }

  /**
   * @param { string } dataString
   * @returns { VectorLayer }
   */
  _créerVectorLayerDepuisDonnéesImportées (dataString) {
    const compteur = this._layerCount + 1;
    const urlGeoserver = dataString.url_geoserver || false;
    const vectorLayer = new VectorLayer({
      id: compteur,
      name: dataString.layer_source_name,
      urlGeoserver: urlGeoserver,
      sourceWorkspace: dataString.layer_source_workspace,
      sourceName: dataString.layer_source_name,
      importedData: dataString
    });
    return vectorLayer;
  }

  /**
   * @description doit appeler l'une après l'autre, comme : this._singleMapPage.addRasterLayer('test OSM', SourceType.OSM).then(v => { this._singleMapPage.addVectorLayer('Districts'); });
   * @param { RasterLayer | VectorLayer } layer
   * @param { number } jsonIndex
   * @returns { TileLayer | VectorTileLayer }
   */
  _ajouterLayerÀListe (layer, jsonIndex = 0) {
    try {
      const _this = this;
      const layers = this._layers;
      const zIndex = jsonIndex === 0 ? layers.size + 1 : jsonIndex;
      const mLayer = layer;
      const olLayer = new Promise((resolve, reject) => {
        resolve(mLayer.olLayer);
      });

      return olLayer.then(olLayerLoaded => {
        olLayerLoaded.setZIndex(zIndex);
        mLayer.zIndex = zIndex;

        _this._écouterÉvénementsOlLayer(olLayerLoaded);
        _this._layers.set(mLayer.id, mLayer);
        _this._notifierLayerAjoutéeDansListe(mLayer);
        _this._écouterLayerStyleChangé(mLayer);

        _this._selectedLayer = _this._layers.get(mLayer.id);
        _this._layersZIndex.set(zIndex, mLayer.id);
        return _this._selectedLayer.olLayer;
      });
    } catch (e) {
      console.log(e);
    }
  }

  /*
   * EXPORTATION VERS IMAGE
   */

  exporterCarteUniqueVersPDF () {
    // exportButton.disabled = true;
    // document.body.style.cursor = 'progress';
    /* const _this = this;
    const size = this._map.getSize();
    const resolution = 72;
    const dim = [279.4, 215.9];
    const width = Math.round(size[0]); // Math.round((dim[0] * resolution) / 25.4);
    const height = Math.round(size[1]); // Math.round((dim[1] * resolution) / 25.4);
    const viewResolution = this._map.getView().getResolution();
    console.log(width + ' ' + height);

    this._map.once('rendercomplete', function () {
      document.body.style.cursor = 'progress';

      const classeCanvas = '.' + _this._targetMap + ' .ol-layer canvas';
      const canvas = document.querySelectorAll(classeCanvas);
      const canvasContext = canvas.getContext('2d');
      const pdf = new JsPDF('landscape');
      pdf.addImage(
        canvas.toDataURL('image/png'),
        'PNG',
        0,
        0,
        1350,
        750
      );

      pdf.save('map.pdf');
      _this._map.setSize(size);
      _this._map.getView().setResolution(viewResolution);
      // exportButton.disabled = false;
      document.body.style.cursor = 'auto';
    });
    this.refreshMap(); */
    $('.export-layout').show();
  }

  /*
   * PUBLIC LISTENERS
   */

  _notifierLayerAjoutéeDansListe (layer) { }

  écouterLayerAjoutéeDansListe (listener) {
    this._notifierLayerAjoutéeDansListe = listener;
  }

  /*
   * PRIVATE LISTENERS
   */

  /**
   * @param { * } olLayer
   * @returns { string }
   */
  _écouterÉvénementsOlLayer (olLayer) {
    const _this = this;
    olLayer.on('change:visible', function () {
      _this._map.renderSync();
    });
    olLayer.on('change:zIndex', function () {
      _this.refreshMap();
    });
    return 'done';
  }

  /** @param { RasterLayer | VectorLayer } layer  */
  _écouterLayerStyleChangé (layer) {
    const _this = this;
    if (layer instanceof VectorLayer) {
      layer.registerLayerStyleChangedListener(function () {
        _this.olMap.render();
      });
    }
  }

  /*
   * JSON SPECIFIC - SAVE
   */

  /** @override */
  toJSON () {
    return {
      id: this.id,
      pageType: this.pageType,
      title: this.title,
      layers: this._convertirLayersEnJSON(),
      layersZIndex: this._convertirLayersZIndexEnJSON(),
      targetMap: this._targetMap,
      mapCenterCoordinates: {
        x: this._view.getCenter()[0],
        y: this._view.getCenter()[1]
      },
      mapZoomLevel: this._view.getZoom(),
      layerCount: this._layerCount,
      layoutPage: this._layoutPage ? this._layoutPage.toJSON() : false
    };
  }

  _convertirLayersEnJSON () {
    const jsonLayers = {};
    for (const [key, value] of this._layers) {
      jsonLayers[key] = { type: value instanceof VectorLayer ? 'vector' : 'raster', json: value.toJSON() };
    }
    return jsonLayers;
  }

  _convertirLayersZIndexEnJSON () {
    const jsonLayersZIndex = [];
    for (const [zIndex, layerId] of this._layersZIndex) {
      jsonLayersZIndex.push({ zIndex: zIndex, layerId: layerId });
    }
    return jsonLayersZIndex;
  }

  /*
   * JSON - CHARGEMENT
   */

  _créerDepuisJSON (jsonSingleMapPage) {
    this._layerCount = jsonSingleMapPage.layerCount;
    this._layers = new Map();
    this._layersZIndex = créerLayersZIndexDepuisJSON(jsonSingleMapPage.layersZIndex);
    this._view = new View({
      projection: EPSG.default,
      center: [jsonSingleMapPage.mapCenterCoordinates.x, jsonSingleMapPage.mapCenterCoordinates.y],
      zoom: jsonSingleMapPage.mapZoomLevel
    });
    this._map = new MapOL({
      controls: defaultControls({
        attribution: false,
        rotate: false,
        zoom: false
      }).extend([
        new ScaleLine({
          units: 'metric'
        })
      ]),
      target: jsonSingleMapPage.targetMap,
      view: this._view
    });
    this._targetMap = jsonSingleMapPage.targetMap;
    console.log('SingleMapPageId = %o, targetMap = %o', this.id, this._targetMap);
    this._layoutPage = créerLayoutPageDepuisJSON(jsonSingleMapPage.layoutPage);
  }

  /**
   * @description exemple d'utilisation dans Project
   * const mapPage = new SingleMapPage({ jsonSingleMapPage: jsonSingleMapPage });
   * this._addSingleMapPageToArraysFromJson(mapPage, layers); --> appelle createLayersFromJSON
   * @param { string } jsonLayers
   * @returns { boolean }
   */
  async createLayersFromJSON (jsonLayers) {
    const _this = this;

    for (let i = 1; i <= this._layersZIndex.size; i++) {
      const layerId = this._layersZIndex.get(i);
      const jsonLayer = jsonLayers[layerId];
      if (jsonLayer.type === 'vector') {
        const vectorLayerJsonDonnéesImportées = await VectorLayer.obtenirDonnéesImportéesDepuisJSON(jsonLayer.json);
        const vectorLayer = new VectorLayer({
          jsonObject: jsonLayer.json,
          jsonDonneesImportees: await vectorLayerJsonDonnéesImportées
        });
        const layer = await _this._ajouterLayerÀListe(await vectorLayer, await vectorLayer.zIndex);
        console.log(await vectorLayer.name + ' : ' + await vectorLayer.zIndex);
        
        _this._map.addLayer(layer);

        if (OPTION_LIBELLÉ) {
          this._ajouterCoucheLabel(vectorLayer, layer.zIndex);
        }

        if (vectorLayer.vectorLayerStyle.styleType === StyleTypes.GRADUATED) {
          const donneesImportees = await vectorLayer.donneesImportees;
          vectorLayer.actualiserStyleDonnéesImportéesJSON(donneesImportees);
        } else {
          vectorLayer.actualiserStyleDonnéesImportées();
        }
      } else {
        const rasterLayer = new RasterLayer({ jsonObject: jsonLayer.json });
        const layer = await _this._ajouterLayerÀListe(await rasterLayer, await rasterLayer.zIndex);
        console.log(await rasterLayer.name + ' : ' + await rasterLayer.zIndex);
        
        _this._map.addLayer(layer);
      }
      _this._map.render();
    }
    return true;
  }
}

/**
 * @param { object } layoutPageJSON
 * @returns { LayoutPage }
 */
function créerLayoutPageDepuisJSON (layoutPageJSON) {
  const layoutPage = new LayoutPage({ objetJSON: layoutPageJSON });

  layoutPage.activerDepuisJSON(layoutPageJSON);
  return layoutPage;
}

/**
 * @param { object } jsonLayersZIndex
 * @returns { Map }
 */
function créerLayersZIndexDepuisJSON (jsonLayersZIndex) {
  const layersZIndex = new Map();
  jsonLayersZIndex.forEach(function (layer) {
    layersZIndex.set(layer.zIndex, layer.layerId);
  });
  return layersZIndex;
}
